/**
 * Mapping configuration for Evaluation Forms
 * Maps Frontend Field IDs -> Backend DTO Keys
 */

import { parseIsoDateToMDYUS } from "../../utils/dateUtils";

export const HHA_MAPPING = {
    // A. Patient Care
    patientCare1a: "personalCareBathsRating",
    patientCare1b: "personalCareBackRubsRating",
    patientCare1c: "personalCareOralHygieneRating",
    patientCare1d: "personalCareShampoosRating",
    patientCare1e: "personalCareChangingBedLinenRating",
    patientCare1f: "personalCareAssistingDressingRating",
    patientCare1g: "personalCareSkinCareRating",
    patientCare1h: "personalCareToiletingRating",
    patientCare1i: "personalCareLivingAreaCleanRating",
    patientCare2: "planningNutritiousMealsRating",
    patientCare3: "assistingFeedingRating",
    patientCare4: "takingRecordingVitalsRating",
    patientCare5: "assistingAmbulationRating",
    patientCare6: "rangeOfMotionProceduresRating",
    patientCare7: "remindingMedicationRating",
    patientCare8: "doingPatientLaundryRating",
    patientCare9: "meetingSafetyNeedsRating",
    patientCare10: "reportingToNurseRating",
    patientCare11: "adheringToDocumentationRating",

    // B. Organizational
    organizational1: "acceptsDirectionRating",
    organizational2: "maintainsWorkRecordRating",
    organizational3: "acceptsResponsibilityRating",
    organizational4: "respectsRightsRating",
    organizational5a: "guidelinesInfectionControlRating",
    organizational5b: "guidelinesFireSafetyRating",
    organizational5c: "guidelinesPatientCareRating",
    organizational6: "equipmentManagementRating",
    organizational7: "participatesQualityActivitiesRating",
    organizational8: "interactsCollaborativelyRating",

    // C. Education/In-Service
    educational1: "cprProgramRating",
    educational2: "safetyEthicsProgramsRating",
    educational3: "inServicesSelfLearningRating",
    educational4: "competencyChecklistRating"
};

export const RN_MAPPING = {
    // A. Patient Care
    patientCare1: "patientCareAssessmentRating",
    patientCare2: "patientCareReevaluationRating",
    patientCare3: "patientCarePlanInitiationRating",
    patientCare4: "patientCareNursingDiagnosisRating",
    patientCare5: "patientCarePlanDevelopmentRating",
    patientCare6: "patientCareProceduresMedicationsRating",
    patientCare7: "patientCareCounselingRating",
    patientCare8: "patientCareInstructionsRating",
    patientCare9: "patientCareDischargePlanningRating",

    // Communication
    communication1: "communicationClinicalNotesRating",
    communication2: "communicationPhysicianReportingRating",
    communication3: "communicationCommunityRating",

    // Additional Duties
    additionalDuty1: "additionalDutiesOnCallRating",
    additionalDuty2: "additionalDutiesEquipmentRating",
    additionalDuty3: "additionalDutiesInstructsSupervisesRating",

    // B. Organizational
    organizational1: "adheresToAssignmentsRating",
    organizational2: "maintainsWorkRecordRating",
    organizational3: "informsCoordinatorAvailabilityRating",
    organizational4: "reviewsPolicyManualRating",
    organizational5: "supervisesLpnsHhaRating",
    organizational6: "acceptsResponsibilityRating",
    organizational7: "respectsRightsRating",
    organizational8a: "guidelinesInfectionControlRating",
    organizational8b: "guidelinesFireSafetyRating",
    organizational8c: "guidelinesPatientCareStandardsRating",
    organizational9: "equipmentManagementRating",
    organizational10: "participatesQualityActivitiesRating",
    organizational11: "interactsCollaborativelyRating",

    // C. Education
    educational1: "cprProgramRating",
    educational2: "safetyEthicsProgramsRating",
    educational3: "inServicesSelfLearningRating",
    educational4: "competencyChecklistRating"
};

export const LVN_MAPPING = {
    // A. Patient Care
    patientCare1: "directPatientCareRating",
    patientCare2: "implementsPlanOfCareRating",
    patientCare3: "documentationRating",
    patientCare4: "patientFamilyEducationRating",
    patientCare5: "coordinationHomeHealthServicesRating",
    patientCare6: "equipmentSuppliesRating",
    patientCare7: "professionalGrowthRating",
    patientCare8: "otherDutiesAssignedRating",

    // B. Organizational
    organizational1: "adheresToAssignmentsRating",
    organizational2: "nursingCareMedicalOrdersRating",
    organizational3: "maintainsWorkRecordRating",
    organizational4: "informsCoordinatorAvailabilityRating",
    organizational5: "reviewsPolicyManualRating",
    organizational6: "acceptsResponsibilityRating",
    organizational7: "respectsRightsRating",
    organizational8a: "guidelinesInfectionControlRating",
    organizational8b: "guidelinesFireSafetyRating",
    organizational8c: "guidelinesPatientCareStandardsRating",
    organizational9: "equipmentManagementRating",
    organizational10: "participatesQualityActivitiesRating",
    organizational11: "interactsCollaborativelyRating",

    // C. Education
    educational1: "cprProgramRating",
    educational2: "safetyEthicsProgramsRating",
    educational3: "inServicesSelfLearningRating",
    educational4: "competencyChecklistRating"
};

// Generic mapping for roles that use dutyX -> dutyXRating (MSW, OT, ST, etc.)
// Helper to generate dynamic mapping if needed, or we can just handle it in logic
export const getDutyMapping = (count) => {
    const map = {};
    for (let i = 1; i <= count; i++) {
        map[`duty${i}`] = `duty${i}Rating`;
    }
    return map;
};

export const MSW_MAPPING = getDutyMapping(25);
export const OT_MAPPING = getDutyMapping(25); // OT/OTA share schema
export const ST_MAPPING = getDutyMapping(26);
export const OA_MAPPING = getDutyMapping(24);

// Master map by role code
export const ROLE_PAYLOAD_MAP = {
    HHA: HHA_MAPPING,
    RN: RN_MAPPING,
    LVN: LVN_MAPPING,
    MSW: MSW_MAPPING,
    OT: OT_MAPPING,
    OTA: OT_MAPPING, // OTA uses same as OT
    ST: ST_MAPPING,
    OA: OA_MAPPING,
    "OFFICE ASSISTANT": OA_MAPPING,
    "OFFICE-ASSISTANT": OA_MAPPING,
    PT: null, // PT handles differently (array of objects)
    PTA: null // PTA handles differently
};

/**
 * REVERSE MAPPING HELPERS
 * Used to map Backend Response -> Frontend Form State
 */

export const mapBackendDataToFrontend = (role, backendData) => {
    if (!backendData) return {};

    // ── Base (non-rating) fields shared across all roles ─────────────────────
    // We build a base result first so PT/PTA can also benefit from it
    const baseResult = {};

    // employeeName stores the employee's numeric ID (used as select value)
    if (backendData.employeeId !== undefined && backendData.employeeId !== null)
        baseResult.employeeName = String(backendData.employeeId);

    // If employee details are provided in a nested object, capture the full name for display
    if (backendData.employee) {
        const { firstName, lastName } = backendData.employee;
        baseResult.employeeLabel = `${firstName || ''} ${lastName || ''}`.trim();
    }

    // reviewType maps from evaluationType (backend) → "Probationary" | "Annual"
    if (backendData.evaluationType) baseResult.reviewType = backendData.evaluationType;

    // Dates – strip the time portion so <input type="date"> accepts them
    if (backendData.evaluationDate)
        baseResult.evaluationDate = backendData.evaluationDate.split('T')[0];
    if (backendData.hireDate)
        baseResult.hireDate = backendData.hireDate.split('T')[0];

    // Reviewer fields
    if (backendData.reviewerName || backendData.adminName || backendData.adminSignatureName)
        baseResult.reviewerName = backendData.reviewerName || backendData.adminName || backendData.adminSignatureName;

    if (backendData.reviewerSignature || backendData.adminSignature)
        baseResult.reviewerSignature = backendData.reviewerSignature || backendData.adminSignature;

    const signatureDate = backendData.reviewerSignedAt || backendData.adminSignatureDate || backendData.reviewedBySignatureDate;
    if (signatureDate) {
        baseResult.reviewerSignedAt = parseIsoDateToMDYUS(signatureDate);
    }

    // Status
    if (backendData.status) baseResult.status = backendData.status;
    // ─────────────────────────────────────────────────────────────────────────

    // PT/PTA Special Handling
    if (role === 'PT' || role === 'PTA') {
        const result = { ...baseResult };
        if (backendData.dutyItems && Array.isArray(backendData.dutyItems)) {
            backendData.dutyItems.forEach(item => {
                // Backend: { dutyIndex: 0, rating: 3 } -> Frontend: duty1: "3"
                const frontendKey = `duty${item.dutyIndex + 1}`;
                result[frontendKey] = String(item.rating);
            });
        }

        // Map PT/PTA comments array
        if (backendData.comments && Array.isArray(backendData.comments)) {
            backendData.comments.forEach(comment => {
                const pageNum = (comment.sortOrder || 0) + 1;
                result[`dutiesCommentPage${pageNum}`] = comment.commentText;
            });
        }

        // Map overallComments
        if (backendData.overallComments) {
            result.overallComments = backendData.overallComments;
        }

        return result;
    }

    const mapping = ROLE_PAYLOAD_MAP[role];
    if (!mapping) return baseResult;

    // Invert the map: BackendKey -> FrontendKey
    const reverseMap = Object.entries(mapping).reduce((acc, [frontKey, backKey]) => {
        acc[backKey] = frontKey;
        return acc;
    }, {});

    const result = { ...baseResult };
    Object.keys(backendData).forEach(backKey => {
        if (reverseMap[backKey]) {
            const val = backendData[backKey];
            if (val !== null && val !== undefined) {
                result[reverseMap[backKey]] = String(val);
            }
        }
    });

    // Also handle non-rating fields (daysTardy / daysAbsent)
    if (backendData.daysTardy !== undefined && backendData.daysTardy !== null)
        result.daysTardy = String(backendData.daysTardy);
    if (backendData.daysAbsent !== undefined && backendData.daysAbsent !== null)
        result.daysAbsent = String(backendData.daysAbsent);

    // Role-specific tardy/absent field names used by the form inputs
    if (backendData.daysTardy) {
        if (role === 'HHA' || role === 'RN') result['organizational2_daysTardy'] = String(backendData.daysTardy);
        if (role === 'LVN') result['organizational3_daysTardy'] = String(backendData.daysTardy);
    }
    if (backendData.daysAbsent) {
        if (role === 'HHA' || role === 'RN') result['organizational2_daysAbsent'] = String(backendData.daysAbsent);
        if (role === 'LVN') result['organizational3_daysAbsent'] = String(backendData.daysAbsent);
    }

    // Handle RN/LVN/HHA comments and goals arrays
    if (role === 'RN' || role === 'LVN' || role === 'HHA' || role === 'MSW' || role === 'OT' || role === 'OTA' || role === 'ST' || role === 'OA' || role === 'OFFICE ASSISTANT' || role === 'OFFICE-ASSISTANT') {
        if (backendData.comments && Array.isArray(backendData.comments)) {
            backendData.comments.forEach(comment => {
                const pageNum = (comment.sortOrder || 0) + 1;
                result[`dutiesCommentPage${pageNum}`] = comment.commentText;
            });
        }
        if (backendData.targetedGoals && Array.isArray(backendData.targetedGoals)) {
            backendData.targetedGoals.forEach(goal => {
                const pageNum = (goal.sortOrder || 0) + 1;
                result[`targetedGoalsPage${pageNum}`] = goal.targetedGoalText;
            });
        }
    }

    return result;
};
